import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemContent,
  ListDivider,
  Sheet,
  Table,
  iconButtonClasses,
  Button,
  IconButton,
  Card,
  Input,
  ButtonGroup,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Option,
  ModalOverflow,
} from "@mui/joy";
import { toast } from "react-toastify";
import { LoadingView, PageTitle } from "../../components";
import { thousandSeparator } from "../../utils";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
  AddOutlined,
  EditOutlined,
  DeleteOutline,
  WarningRounded,
} from "@mui/icons-material";
import {
  listTarrifRequest,
  listTarrifReset,
  createTarrifRequest,
  createTarrifReset,
  updateTarrifRequest,
  updateTarrifReset,
  deleteTarrifRequest,
  deleteTarrifReset,
} from "../../store/actions";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { STATUS } from "../../constant";

const TRANS_TYPE_LABEL = {
  1: "Deposit",
  2: "Withdrawal",
};

const formatAmount = (value) => {
  const num = Number(value);
  if (value === null || value === undefined || value === "" || !num) {
    return "∞";
  }
  return thousandSeparator(num);
};

const formatNumberInput = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? thousandSeparator(digits) : "";
};

const unformatNumber = (value) => String(value ?? "").replace(/,/g, "");

const MobileViewTable = ({ data, props }) => {
  return (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      {data.map((listItem, index) => (
        <List key={index} size="sm" sx={{ "--ListItem-paddingX": 0 }}>
          <ListItem
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <ListItemContent
              sx={{ display: "flex", gap: 2, alignItems: "start" }}
            >
              <div>
                <Typography fontWeight={600} gutterBottom>
                  {TRANS_TYPE_LABEL[listItem.transaction_type]}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Min: </b> {formatAmount(listItem.min_amount)}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Max: </b> {formatAmount(listItem.max_amount)}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Commission: </b> {formatAmount(listItem.agent_commission)}
                </Typography>
              </div>
            </ListItemContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                rowGap: 1,
              }}
            >
              <ButtonGroup variant="soft" size="sm">
                <Button color="primary" onClick={() => props.edit(listItem)}>
                  <EditOutlined />
                </Button>
                <Button color="danger" onClick={() => props.remove(listItem)}>
                  <DeleteOutline />
                </Button>
              </ButtonGroup>
            </Box>
          </ListItem>
          <ListDivider />
        </List>
      ))}
    </Box>
  );
};

const DesktopViewTable = ({ data, props }) => {
  return (
    <Sheet
      variant="outlined"
      sx={{
        display: { xs: "none", md: "flex" },
        borderRadius: "sm",
        flexShrink: 1,
        overflow: "auto",
        minHeight: 0,
      }}
    >
      <Table
        aria-labelledby="tableTitle"
        stickyHeader
        hoverRow
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          "--TableCell-paddingY": "4px",
          "--TableCell-paddingX": "8px",
          "& tr > *:last-child": {
            position: "sticky",
            right: 0,
            bgcolor: "var(--TableCell-headBackground)",
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 120, padding: "10px 6px" }}>Trans Type</th>
            <th style={{ width: 120, padding: "10px 6px" }}>Min Amount</th>
            <th style={{ width: 120, padding: "10px 6px" }}>Max Amount</th>
            <th style={{ width: 150, padding: "10px 6px" }}>Agent Commission</th>
            <th aria-label="last" style={{ width: 120, padding: "10px 6px" }} />
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>
                <Typography level="body-sm">
                  {TRANS_TYPE_LABEL[row.transaction_type]}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm">
                  {formatAmount(row.min_amount)}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm">
                  {formatAmount(row.max_amount)}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm">
                  {formatAmount(row.agent_commission)}
                </Typography>
              </td>
              <td>
                <ButtonGroup variant="soft" size="sm">
                  <Button
                    title="Edit"
                    color="primary"
                    onClick={() => props.edit(row)}
                  >
                    <EditOutlined />
                  </Button>
                  <Button
                    title="Delete"
                    color="danger"
                    onClick={() => props.remove(row)}
                  >
                    <DeleteOutline />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

const TarrifPage = ({
  loginResult,
  listTarrifStatus,
  listTarrifResult,
  listTarrifErrorMessage,
  createTarrifStatus,
  createTarrifErrorMessage,
  updateTarrifStatus,
  updateTarrifErrorMessage,
  deleteTarrifStatus,
  deleteTarrifErrorMessage,
}) => {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery("(min-width:600px)");
  const { state: routeState } = useLocation();
  const { partner_id, partner_name, fsp_code } = routeState || {};
  const routePartner = { id: partner_id, name: partner_name, fsp_code };

  const [currentPage, setCurrentPage] = useState(0);
  const [activeTab, setActiveTab] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const [formModal, setFormModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [fspCode, setFspCode] = useState("");
  const [formPartner, setFormPartner] = useState(routePartner);
  const [transType, setTransType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [agentCommission, setAgentCommission] = useState("");

  useEffect(() => {
    dispatch(listTarrifReset());
    if (fsp_code) {
      dispatch(listTarrifRequest(loginResult.token, { fsp_code }));
    }
  }, [dispatch, loginResult, fsp_code]);

  useEffect(() => {
    if (listTarrifStatus === STATUS.ERROR) {
      toast.error(listTarrifErrorMessage);
      dispatch(listTarrifReset());
    }
  }, [listTarrifStatus, listTarrifErrorMessage, dispatch]);

  useEffect(() => {
    if (createTarrifStatus === STATUS.ERROR) {
      toast.error(createTarrifErrorMessage);
      dispatch(createTarrifReset());
    }
    if (createTarrifStatus === STATUS.SUCCESS) {
      toast.success("Tarrif created successfully");
      setFormModal(false);
      resetForm();
      dispatch(createTarrifReset());
      dispatch(
        listTarrifRequest(loginResult.token, {
          ...(fsp_code ? { fsp_code } : {}),
        }),
      );
    }
  }, [createTarrifStatus, createTarrifErrorMessage, dispatch, loginResult, fsp_code]);

  useEffect(() => {
    if (updateTarrifStatus === STATUS.ERROR) {
      toast.error(updateTarrifErrorMessage);
      dispatch(updateTarrifReset());
    }
    if (updateTarrifStatus === STATUS.SUCCESS) {
      toast.success("Tarrif updated successfully");
      setFormModal(false);
      resetForm();
      dispatch(updateTarrifReset());
      dispatch(
        listTarrifRequest(loginResult.token, {
          ...(fsp_code ? { fsp_code } : {}),
        }),
      );
    }
  }, [updateTarrifStatus, updateTarrifErrorMessage, dispatch, loginResult, fsp_code]);

  useEffect(() => {
    if (deleteTarrifStatus === STATUS.ERROR) {
      toast.error(deleteTarrifErrorMessage);
      dispatch(deleteTarrifReset());
    }
    if (deleteTarrifStatus === STATUS.SUCCESS) {
      toast.success("Tarrif deleted successfully");
      setDeleteModal(false);
      setDeleteItem(null);
      dispatch(deleteTarrifReset());
      dispatch(
        listTarrifRequest(loginResult.token, {
          ...(fsp_code ? { fsp_code } : {}),
        }),
      );
    }
  }, [deleteTarrifStatus, deleteTarrifErrorMessage, dispatch, loginResult, fsp_code]);

  const resetForm = () => {
    setEditId(null);
    setFspCode("");
    setFormPartner(routePartner);
    setTransType("");
    setMinAmount("");
    setMaxAmount("");
    setAgentCommission("");
  };

  const tarrifData = listTarrifResult || [];

  const filteredData = tarrifData.filter(
    (item) =>
      (item.transaction_type === 1 || item.transaction_type === 2) &&
      item.transaction_type === activeTab,
  );

  const pageLength = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const checkLoading = () => {
    if (
      listTarrifStatus === STATUS.LOADING ||
      createTarrifStatus === STATUS.LOADING ||
      updateTarrifStatus === STATUS.LOADING ||
      deleteTarrifStatus === STATUS.LOADING
    ) {
      return true;
    }
    return false;
  };

  const openCreateModal = () => {
    resetForm();
    setFormModal(true);
  };

  const openEditModal = (item) => {
    setEditId(item.id);
    setFspCode(item.fsp_code || "");
    setFormPartner(routePartner);
    setTransType(item.transaction_type || "");
    setMinAmount(
      item.min_amount ? formatNumberInput(String(item.min_amount)) : "",
    );
    setMaxAmount(
      item.max_amount ? formatNumberInput(String(item.max_amount)) : "",
    );
    setAgentCommission(
      item.agent_commission ? formatNumberInput(String(item.agent_commission)) : "",
    );
    setFormModal(true);
  };

  const openDeleteModal = (item) => {
    setDeleteItem(item);
    setDeleteModal(true);
  };

  const submitForm = (e) => {
    e.preventDefault();
    const data = {
      fsp_code: routePartner?.fsp_code || fspCode || "",
      transaction_type: Number(transType),
      min_amount: Number(unformatNumber(minAmount)),
      max_amount: Number(unformatNumber(maxAmount)),
      agent_commission: Number(unformatNumber(agentCommission)),
    };
    if (editId) {
      dispatch(updateTarrifRequest(loginResult.token, { id: editId, ...data }));
    } else {
      dispatch(createTarrifRequest(loginResult.token, data));
    }
  };

  const confirmDelete = () => {
    dispatch(deleteTarrifRequest(loginResult.token, { id: deleteItem.id }));
  };

  return (
    <Box>
      <PageTitle
        title={partner_name ? `Tarrifs - ${partner_name}` : "Tarrifs"}
      />
      <LoadingView loading={checkLoading()} />

      <Sheet
        sx={{
          display: "flex",
          my: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography level="title-md" sx={{ flexGrow: 1 }}>
          {partner_name || "Select a partner"}
        </Typography>

        {partner_name && (
          <Button
            startDecorator={<AddOutlined />}
            onClick={openCreateModal}
            sx={{ borderRadius: 20 }}
          >
            Add Tarrif
          </Button>
        )}
      </Sheet>

      {partner_name && (
        <Box sx={{ gap: 0.5, display: "flex", my: 1 }}>
          <Button
            variant={activeTab === 1 ? "solid" : "outlined"}
            onClick={() => {
              setActiveTab(1);
              setCurrentPage(0);
            }}
            sx={{ borderRadius: 20 }}
          >
            Deposit
          </Button>
          <Button
            variant={activeTab === 2 ? "solid" : "outlined"}
            onClick={() => {
              setActiveTab(2);
              setCurrentPage(0);
            }}
            sx={{ borderRadius: 20 }}
          >
            Withdrawal
          </Button>
        </Box>
      )}

      {partner_name &&
        (filteredData.length > 0 ? (
        <>
          <MobileViewTable
            data={paginatedData}
            props={{ edit: openEditModal, remove: openDeleteModal }}
          />
          <DesktopViewTable
            data={paginatedData}
            props={{ edit: openEditModal, remove: openDeleteModal }}
          />

          {filteredData.length > ITEMS_PER_PAGE && (
            <Box
              sx={{
                pt: 2,
                gap: 1,
                [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
                display: "flex",
              }}
            >
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<KeyboardArrowLeftIcon />}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                {isDesktop ? "Previous" : ""}
              </Button>

              <Box sx={{ flex: 1 }} />

              {Array.from({ length: pageLength }).map((_, page) => (
                <IconButton
                  key={page}
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  onClick={() => setCurrentPage(page)}
                  disabled={currentPage === page}
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {page + 1}
                </IconButton>
              ))}

              <Typography
                level="body-sm"
                mx="auto"
                textAlign="center"
                sx={{ display: { xs: "flex", md: "none" } }}
              >
                Page {currentPage + 1} of {pageLength}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                endDecorator={<KeyboardArrowRightIcon />}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, pageLength - 1))
                }
                disabled={currentPage === pageLength - 1}
              >
                {isDesktop ? "Next" : ""}
              </Button>
            </Box>
          )}
        </>
        ) : (
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              top: "20%",
            }}
          >
            <WarningRounded
              sx={{ fontSize: "50px", color: "background.appcolor" }}
            />
            <Typography level="title-md">No Tarrifs Found</Typography>
            <Typography level="body-sm" textAlign="center">
              No tarrifs found for {partner_name}. Use Add Tarrif to create
              one.
            </Typography>
          </Card>
        ))}

      {/* Create/Edit Modal */}
      <Modal open={formModal} onClose={() => setFormModal(false)}>
        <ModalOverflow sx={{ pt: "70px" }}>
          <ModalDialog
            sx={(theme) => ({
              [theme.breakpoints.only("xs")]: {
                bottom: 0,
                transition: "0.4s",
                width: "100%",
                maxHeight: "90%",
                paddingBottom: "2rem",
              },
            })}
          >
            <ModalClose onClick={() => setFormModal(false)} />
            <DialogTitle>{editId ? "Edit Tarrif" : "Add Tarrif"}</DialogTitle>
            <DialogContent>
              {editId
                ? "Fill in the form to update the tarrif details."
                : "Fill in the form to add a new tarrif."}
            </DialogContent>
            <Stack component="form" onSubmit={submitForm} gap={2} sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>Partner</FormLabel>
                <Input
                  value={formPartner?.name || partner_name || ""}
                  readOnly
                />
              </FormControl>
              <FormControl>
                <FormLabel>Transaction Type</FormLabel>
                <Select
                  placeholder="Select transaction type"
                  value={transType}
                  onChange={(_, val) => setTransType(val)}
                >
                  <Option value={1}>Deposit</Option>
                  <Option value={2}>Withdrawal</Option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Min Amount</FormLabel>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter minimum amount"
                  value={minAmount}
                  onChange={(e) => setMinAmount(formatNumberInput(e.target.value))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max Amount</FormLabel>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter maximum amount"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(formatNumberInput(e.target.value))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Agent Commission</FormLabel>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter agent commission"
                  value={agentCommission}
                  onChange={(e) =>
                    setAgentCommission(formatNumberInput(e.target.value))
                  }
                />
              </FormControl>
              <Button type="submit" fullWidth>
                {editId ? "Update" : "Submit"}
              </Button>
            </Stack>
          </ModalDialog>
        </ModalOverflow>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModal} onClose={() => setDeleteModal(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose onClick={() => setDeleteModal(false)} />
          <DialogTitle>Delete Tarrif</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this tarrif? This action cannot be
            undone.
          </DialogContent>
          <Stack direction="row" gap={2} sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

const mapStateToProps = ({ auth, tarrif }) => {
  const { otpResult: loginResult } = auth;
  const {
    listTarrifStatus,
    listTarrifResult,
    listTarrifErrorMessage,
    createTarrifStatus,
    createTarrifErrorMessage,
    updateTarrifStatus,
    updateTarrifErrorMessage,
    deleteTarrifStatus,
    deleteTarrifErrorMessage,
  } = tarrif;
  return {
    loginResult,
    listTarrifStatus,
    listTarrifResult,
    listTarrifErrorMessage,
    createTarrifStatus,
    createTarrifErrorMessage,
    updateTarrifStatus,
    updateTarrifErrorMessage,
    deleteTarrifStatus,
    deleteTarrifErrorMessage,
  };
};

export default connect(mapStateToProps, {})(TarrifPage);
